import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMyProductFilterOptions } from '~/api/my-products';
import { useGetProductFilterOptions } from '~/api/product';
import { Input } from '~/components/ui';
import { SavedViewsFilterControls } from '~/components/ui/SavedViewsFilterControls';
import {
  MY_PRODUCTS_FILTER_VIEWS,
  type MyProductsFilterView,
} from '~/pages/my-products/lib/filterViews';
import { getMyProductsFilterFields } from '~/pages/my-products/lib/myProductsFilterFields';
import {
  activeRowCount,
  categoryOf,
  withoutCategory,
} from '~/pages/my-products/lib/myProductsFilterRows';
import type { MyProductsSavedViews } from '~/pages/my-products/lib/useMyProductsSavedViews';
import { MyProductsFilterPanel } from './MyProductsFilterPanel';

type MyProductsToolbarProps = {
  readonly view: MyProductsFilterView;
  readonly views: MyProductsSavedViews;
  readonly searchTerm: string;
  readonly onSearchChange: (value: string) => void;
};

export function MyProductsToolbar({
  view,
  views,
  searchTerm,
  onSearchChange,
}: MyProductsToolbarProps) {
  const { t } = useTranslation('my-product');
  const { t: tInventory } = useTranslation('inventory');
  const category = categoryOf(views.rows);
  const { data: propertyData } = useGetProductFilterOptions(category);
  const { data: myProductFilterData } = useGetMyProductFilterOptions();

  const fieldOptions = useMemo(
    () =>
      getMyProductsFilterFields({
        t,
        tInventory,
        view,
        filterOptions: myProductFilterData,
        propertyKeys: category ? (propertyData?.productPropertyKeys ?? []) : [],
      }),
    [t, tInventory, view, myProductFilterData, category, propertyData]
  );

  return (
    <>
      <div className="flex items-center gap-4 flex-wrap sm:justify-end">
        <SavedViewsFilterControls
          namespace="my-product"
          keyPrefix="filter"
          views={views}
          activeCount={activeRowCount(
            views.rows,
            MY_PRODUCTS_FILTER_VIEWS[view].fields
          )}
          visibleRowCount={withoutCategory(views.rows).length}
          popoverAlign="start"
          buttonTestId="my-products-filter-button"
          countTestId="my-products-filter-count"
          renderPanel={({ maxHeight, bottomContent }) => (
            <MyProductsFilterPanel
              fieldOptions={fieldOptions}
              rows={views.rows}
              maxHeight={maxHeight}
              bottomContent={bottomContent}
              onRowsChange={views.setRows}
              onClearAll={views.clearActiveView}
            />
          )}
        />
      </div>
      <div className="relative min-w-[300px]">
        <Input
          name="searchTerm"
          value={searchTerm}
          onChange={(_, value) => onSearchChange(String(value))}
          placeholder={t('filter.search_placeholder')}
          leftIcon={<Search size={18} className="text-neut-sec" />}
          className="w-full py-2.5 text-sm font-normal"
          containerClassName="w-[80%] sm:w-full"
        />
      </div>
    </>
  );
}
